import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import type { RefObject } from "preact";

/**
 * How long a layer's exit may take before it is unmounted anyway. The exits in src/styles.css run
 * 160ms (--exit-time; the lightbox's fade 150ms); this is the fallback for a missed animationend, not the normal path.
 */
export const EXIT_FALLBACK_MS = 200;

/** Whether this device asked for reduced motion: then a close is immediate, with no wait at all. */
export function prefersReducedMotion(): boolean {
  try {
    return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export interface Exit<A extends unknown[]> {
  /** True from the close until the unmount: put `is-closing` on the layer's root and stop listening. */
  closing: boolean;
  /** The one close: Escape, the backdrop and the Close button all call this. */
  close: (...args: A) => void;
  /** Put on the layer's root; its exit's animationend (or the fallback timer) ends the close. */
  ref: RefObject<HTMLDivElement>;
  /** `is-closing` while closing, else "", to append to the root's class. */
  cls: string;
}

/**
 * A sheet, prompt, viewer or tour that leaves the way it came (docs/rebrand-ux.md: short stepped
 * motion): `close()` adds `is-closing`, the layer plays its stepped exit, and `onClose` runs when
 * that ends (its animationend, or EXIT_FALLBACK_MS, whichever is first). With reduced motion it runs
 * at once. A second close while one is running is ignored, so a backdrop tap and Escape together
 * close once.
 */
export function useExit<A extends unknown[] = []>(onClose: (...args: A) => void): Exit<A> {
  const [closing, setClosing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const latest = useRef(onClose);
  latest.current = onClose;
  const state = useRef<{ done: boolean; timer: number | null; off: (() => void) | null; mounted: boolean }>({
    done: false,
    timer: null,
    off: null,
    mounted: true,
  });

  useEffect(() => {
    const s = state.current;
    s.mounted = true;
    return () => {
      s.mounted = false;
      if (s.timer !== null) window.clearTimeout(s.timer);
      s.off?.();
    };
  }, []);

  const close = useCallback((...args: A) => {
    const s = state.current;
    if (s.done) return;
    s.done = true;
    const finish = () => {
      if (s.timer !== null) window.clearTimeout(s.timer);
      s.timer = null;
      s.off?.();
      s.off = null;
      if (!s.mounted) return;
      latest.current(...args);
      // A layer that stays mounted (the photo check is one) can close again next time.
      s.done = false;
      setClosing(false);
    };
    if (prefersReducedMotion() || !ref.current) {
      finish();
      return;
    }
    const el = ref.current;
    const onEnd = (e: AnimationEvent) => {
      if (e.animationName.endsWith("-exit")) finish();
    };
    el.addEventListener("animationend", onEnd);
    s.off = () => el.removeEventListener("animationend", onEnd);
    s.timer = window.setTimeout(finish, EXIT_FALLBACK_MS);
    setClosing(true);
  }, []);

  return { closing, close, ref, cls: closing ? " is-closing" : "" };
}
